'use-client';
import * as React from 'react';
import type { AnimationItem } from 'lottie-web';
import lottie from 'lottie-web';
import { useState, useEffect, useRef, useImperativeHandle } from 'react';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import PauseResumeButton from '../../PauseResumeButton/PauseResumeButton';
import type {
  ILottieEmbedCompProps,
  ILottieEmbedImperativeActions,
} from '../LottieEmbed.types';
import { addSelfDestructingEventListener } from '../utils';
import semanticClassNames from '../LottieEmbed.semanticClassNames';
import { classes, st } from './LottieEmbed.component.st.css';
import loader from './loader.json';

const noop = () => {};

type LottieEmbedState = {
  animationData: object;
};

const useControlRendered = (showPlaybackControl: boolean) => {
  const [, setControlKey] = useState(0);
  const rerenderControl = React.useCallback(() => {
    if (showPlaybackControl) {
      setControlKey(prev => prev + 1);
    }
  }, [showPlaybackControl]);

  return { rerenderControl };
};

const LottieEmbed: React.ForwardRefRenderFunction<
  ILottieEmbedImperativeActions,
  ILottieEmbedCompProps
> = (props, ref) => {
  const {
    id,
    customClassNames = [],
    animationUrl,
    animationJson,
    loop,
    autoplay,
    speed,
    direction,
    alt,
    onClick = noop,
    onDblClick = noop,
    onBlur = noop,
    onFocus = noop,
    reducedMotion,
    className,
    translations,
    showPlaybackControl,
  } = props;
  const lottieAnimationRef: React.MutableRefObject<AnimationItem | null> =
    useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [animationData, setAnimationData] =
    useState<LottieEmbedState['animationData']>(loader);
  const { rerenderControl } = useControlRendered(showPlaybackControl);

  const lottieId = `lottie-${id}`;

  const setA11yOnLottieElement = () => {
    const lottieElement = document.getElementById(lottieId);
    if (!lottieElement) {
      return;
    }

    if (alt === '') {
      lottieElement.setAttribute('aria-hidden', 'true');
    } else {
      lottieElement.setAttribute('aria-label', alt);
      lottieElement.setAttribute('role', 'img');
    }
  };

  const handlePlayControlClick = () => {
    lottieAnimationRef.current?.play();
    rerenderControl();
  };

  const handlePauseControlClick = () => {
    lottieAnimationRef.current?.pause();
    rerenderControl();
  };

  useEffect(() => {
    if (lottieAnimationRef.current) {
      lottieAnimationRef.current.destroy();
    }

    lottieAnimationRef.current = lottie.loadAnimation({
      container: containerRef.current!,
      renderer: 'svg',
      loop,
      autoplay: !reducedMotion && autoplay,
      animationData,
      rendererSettings: {
        title: alt,
        preserveAspectRatio: 'xMidYMid meet',
        viewBoxOnly: true,
        className: classes.svg,
        // @ts-expect-error. Used to prevent XSS - ECL-9523
        runExpressions: false,
        id: lottieId,
      },
    });
    lottieAnimationRef.current?.addEventListener(
      'DOMLoaded',
      setA11yOnLottieElement,
    );
    lottieAnimationRef.current?.addEventListener('complete', rerenderControl);
    lottieAnimationRef.current?.setSpeed(speed);
    rerenderControl();

    return () => {
      lottieAnimationRef.current?.removeEventListener(
        'DOMLoaded',
        setA11yOnLottieElement,
      );
      lottieAnimationRef.current?.removeEventListener(
        'complete',
        rerenderControl,
      );
      lottieAnimationRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationData, autoplay, loop]);

  useEffect(() => {
    const fetchAnimationData = async () => {
      const response = await fetch(animationUrl);
      const data = await response.json();
      setAnimationData(data);
    };

    if (animationUrl) {
      void fetchAnimationData();
    }
    if (animationJson) {
      setAnimationData(animationJson);
    }
  }, [animationUrl, animationJson]);

  useEffect(() => {
    lottieAnimationRef.current?.setSpeed(speed);
  }, [speed]);

  useEffect(() => {
    lottieAnimationRef.current?.setDirection(direction === 'forward' ? 1 : -1);
  }, [direction]);

  useImperativeHandle(ref, () => ({
    play: () => {
      lottieAnimationRef.current?.play();
      rerenderControl();
      return new Promise(resolve => {
        addSelfDestructingEventListener(
          lottieAnimationRef.current!,
          'complete',
          () => {
            resolve();
            lottieAnimationRef.current?.goToAndStop(0, false);
          },
        );
      });
    },
    stop: () => {
      lottieAnimationRef.current?.stop();
      rerenderControl();
    },
    pause: () => {
      lottieAnimationRef.current?.pause();
      rerenderControl();
    },
    focus: () => {
      containerRef.current?.focus();
    },
    blur: () => {
      containerRef.current?.blur();
    },
  }));

  return (
    <div
      id={id}
      className={st(
        classes.container,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      onClick={onClick}
      onDoubleClick={onDblClick}
      onFocus={onFocus}
      onBlur={onBlur}
      {...getDataAttributes(props)}
    >
      <div ref={containerRef} />
      {showPlaybackControl && lottieAnimationRef.current?.isLoaded ? (
        <PauseResumeButton
          className={classes.playbackControl}
          state={lottieAnimationRef.current.isPaused ? 'paused' : 'playing'}
          ariaControls={lottieId}
          pauseAriaLabel={translations.pauseControlAriaLabel}
          resumeAriaLabel={translations.playControlAriaLabel}
          onPause={handlePauseControlClick}
          onResume={handlePlayControlClick}
        />
      ) : null}
    </div>
  );
};

export default React.forwardRef(LottieEmbed);
